import { createContext, useCallback, useContext, useState } from 'react'
import { ConfirmDialog } from './ui.jsx'

/**
 * App-wide replacement for the browser's native `confirm()`.
 *
 *   const confirm = useConfirm()
 *   const ok = await confirm({
 *     title:        'ลบโลโก้บริษัท?',
 *     message:      'การลบโลโก้ไม่สามารถย้อนกลับได้',
 *     confirmLabel: 'ลบ',
 *     tone:         'danger',          // 'danger' | 'warn' | 'info'
 *   })
 *   if (!ok) return
 *
 * The dialog uses the same ConfirmDialog primitive as the per-page delete
 * flows (Leads / Companies / FollowUps), so the visual treatment is consistent.
 */
const ConfirmContext = createContext(null)

export function useConfirm() {
  const fn = useContext(ConfirmContext)
  if (!fn) throw new Error('useConfirm must be used inside <ConfirmProvider>')
  return fn
}

export default function ConfirmProvider({ children }) {
  const [opts, setOpts] = useState(null)

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setOpts({
        title:        options.title        || 'Are you sure?',
        message:      options.message      || '',
        confirmLabel: options.confirmLabel || 'ยืนยัน',
        cancelLabel:  options.cancelLabel  || 'ยกเลิก',
        tone:         options.tone         || 'danger',
        _resolve:     resolve,
      })
    })
  }, [])

  function close(value) {
    if (opts?._resolve) opts._resolve(value)
    setOpts(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={!!opts}
        title={opts?.title}
        message={opts?.message}
        confirmLabel={opts?.confirmLabel}
        cancelLabel={opts?.cancelLabel}
        tone={opts?.tone}
        onCancel={() => close(false)}
        onConfirm={() => close(true)}
      />
    </ConfirmContext.Provider>
  )
}
